import { components as c } from "@/components";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import EducationFormFields from "@/features/events/features/educations/EducationFormFields";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateLocks } from "@/features/templates/hooks/useTemplateLocks";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { templateSettingsSchema } from "@patient-watch/types";
import { isUuid } from "@/services/isUuid";
import { useAuth } from "@/utils/auth";
import { useClinicianOptions } from "@/features/auth/api/cliniciansByOrganisationQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

// Dedicated schema for education form
const educationFormSchema = z.object({
    education_name: z.string().min(1, "Please provide a name for this consultation"),
    education_description: z.string().min(1, "Please describe what was discussed or taught"),
    educated_by: z.string().uuid("Please select who provided the consultation").optional(),
});

type EducationFormValues = z.infer<typeof educationFormSchema>;

const EducationFormFieldsPage = () => {
    const auth = useAuth();
    const { templateId } = Route.useParams();
    const educatedByLocked = useTemplateLocks(templateId).isLocked("educated_by");
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const education_name = search.education_name;
    const education_description = search.education_description;
    const educated_by = search.educated_by;

    const { markStepComplete, nextStep, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const navigate = useNavigate();
    const templatesCopy = useTemplatesCopy();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search
    );

    const form = useForm<EducationFormValues>({
        resolver: zodResolver(educationFormSchema),
        defaultValues: {
            education_name: "",
            education_description: "",
            educated_by: "",
        },
    });

    const clinicianOptions = useClinicianOptions();

    useEffect(() => {
        setCurrentStep("education");
    }, [setCurrentStep]);

    // Set form values after options load - using reset to ensure ComboBox recognizes values
    useEffect(() => {
        if (clinicianOptions.length > 0) {
            const educatedByValue =
                educated_by && isUuid(educated_by) ? educated_by : educatedByLocked ? "" : auth?.id || "";

            form.reset({
                education_name: education_name ?? "",
                education_description: education_description ?? "",
                educated_by: educatedByValue,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicianOptions.length, educated_by, education_name, education_description, auth?.id, educatedByLocked]);

    const handleNext = (formData: EducationFormValues) => {
        markStepComplete(currentStep);
        const nextRoute = getNextStepRoute(currentStep, { templateId });
        if (nextRoute) {
            nextStep();
            const nextSettings = { ...search, ...formData };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            navigate({
                ...nextRoute,
                search: wizardSearchPayload,
            });
        } else {
            finalize(formData);
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            const nextSettings = { ...search, ...form.getValues() };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            navigate({
                ...prevRoute,
                search: wizardSearchPayload,
            });
        }
    };

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleNext)} className="space-y-6">
                <EducationFormFields />

                <TemplateStepFooter
                    onBack={handlePrevious}
                    submitPrimary
                    primaryAction="save"
                    primaryLabel={templatesCopy.saveTemplate}
                    isLoadingPrimary={isSubmitting}
                    disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
                />
            </form>
        </c.ui.Form>
    );
};

const educationSearchSchema = templateSettingsSchema.partial().pick({
    education_name: true,
    education_description: true,
    educated_by: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/education/")({
    component: EducationFormFieldsPage,
    validateSearch: (search) => educationSearchSchema.parse(search),
});
